import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, getHotelCollectionPath, waitForAuth, staffLoginApi, signInWithToken, firebaseSignOut } from '../firebase';
import { MinimalStaffSession, StaffRole } from '../types/restaurantStaff';

interface StaffUser {
  id: string;
  email: string;
  name: string;
  restaurantId: string;
  restaurantName: string;
  role: StaffRole;
  hotelId: string;
}

interface RestaurantAuthContextType {
  staff: StaffUser | null;
  hotelId: string;
  restaurantId: string;
  restaurantName: string | null;
  restaurantImage: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

export const RestaurantAuthContext = createContext<RestaurantAuthContextType | undefined>(undefined);

const getSessionKey = (hotelId: string, restaurantId: string) =>
  `me_restaurant_session_${hotelId}_${restaurantId}`;

const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const REMEMBER_ME_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

interface RestaurantAuthProviderProps {
  children: ReactNode;
  hotelId: string;
  restaurantId: string;
}

export function RestaurantAuthProvider({ children, hotelId, restaurantId }: RestaurantAuthProviderProps) {
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [restaurantImage, setRestaurantImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load restaurant info and check session on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        // GD-004: Wait for Firebase auth state to settle (custom token session persists)
        const hasAuth = await waitForAuth();

        let loadedRestaurantName: string | null = null;

        // Load restaurant name and image — public info, no auth required
        try {
          const restaurantsPath = getHotelCollectionPath(hotelId, 'restaurants');
          const restaurantDoc = await getDoc(doc(db, restaurantsPath, restaurantId));
          if (restaurantDoc.exists()) {
            const data = restaurantDoc.data();
            const name = typeof data.name === 'string'
              ? data.name
              : data.name?.en || restaurantId;
            loadedRestaurantName = name;
            setRestaurantName(name);
            const image = data.gallery?.[0] || data.imageUrl || null;
            setRestaurantImage(image);
          }
        } catch {
          // Firestore read may fail — restaurant info is non-critical for login
        }

        // Check for existing session (GD-003: minimal session, no PII in localStorage)
        const sessionKey = getSessionKey(hotelId, restaurantId);
        const stored = localStorage.getItem(sessionKey);
        if (stored) {
          const parsed = JSON.parse(stored);

          // Backwards compatibility: detect old format with PII and clear it
          if (parsed.staff) {
            localStorage.removeItem(sessionKey);
          } else if (
            parsed.expiry > Date.now() &&
            parsed.hotelId === hotelId &&
            parsed.restaurantId === restaurantId
          ) {
            // New minimal format — re-fetch staff data from Firestore
            // GD-004: Requires valid Firebase auth (custom token session)
            if (hasAuth) {
              try {
                const staffPath = getHotelCollectionPath(hotelId, 'restaurant_staff');
                const staffDocRef = doc(db, staffPath, parsed.staffId);
                const staffSnapshot = await getDoc(staffDocRef);

                if (staffSnapshot.exists()) {
                  const staffData = staffSnapshot.data();
                  if (staffData.isActive !== false) {
                    setStaff({
                      id: parsed.staffId,
                      email: staffData.email,
                      name: staffData.name,
                      restaurantId: staffData.restaurantId,
                      restaurantName: staffData.restaurantName || loadedRestaurantName || restaurantId,
                      role: staffData.role,
                      hotelId,
                    });
                  } else {
                    // Staff deactivated — clear session
                    localStorage.removeItem(sessionKey);
                    await firebaseSignOut();
                  }
                } else {
                  localStorage.removeItem(sessionKey);
                  await firebaseSignOut();
                }
              } catch {
                // Firestore fetch failed — clear session and require re-login
                localStorage.removeItem(sessionKey);
                await firebaseSignOut();
              }
            } else {
              // No Firebase auth session — clear local session, require re-login
              localStorage.removeItem(sessionKey);
            }
          } else {
            // Expired session
            localStorage.removeItem(sessionKey);
            await firebaseSignOut();
          }
        }
      } catch (error) {
        console.error('Error initializing restaurant auth:', error);
      }
      setIsLoading(false);
    };

    initialize();
  }, [hotelId, restaurantId]);

  // GD-004: Login via server-side API instead of client-side Firestore queries.
  // Password verification and credential lookup happen on the server.
  // The server returns a Firebase custom token with hotel/restaurant claims.
  const login = async (email: string, password: string, rememberMe?: boolean): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await staffLoginApi({
        email,
        password,
        hotelId,
        restaurantId,
      });

      if (!result.success || !result.token || !result.staff) {
        return { success: false, error: result.error || 'Invalid email or password' };
      }

      // Sign in to Firebase with the custom token — enables Firestore access with claims
      await signInWithToken(result.token);

      const staffUser: StaffUser = {
        id: result.staff.id,
        email: result.staff.email,
        name: result.staff.name,
        restaurantId: result.staff.restaurantId,
        restaurantName: result.staff.restaurantName || restaurantName || restaurantId,
        role: result.staff.role as StaffRole,
        hotelId,
      };

      // GD-003: Store only minimal session data (no PII) in localStorage
      const minimalSession: MinimalStaffSession = {
        staffId: result.staff.id,
        hotelId,
        restaurantId: result.staff.restaurantId,
        expiry: Date.now() + (rememberMe ? REMEMBER_ME_DURATION : SESSION_DURATION),
      };

      localStorage.setItem(getSessionKey(hotelId, restaurantId), JSON.stringify(minimalSession));
      setStaff(staffUser);

      if (result.staff.restaurantName) {
        setRestaurantName(result.staff.restaurantName);
      }

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred. Please try again.' };
    }
  };

  const logout = async () => {
    localStorage.removeItem(getSessionKey(hotelId, restaurantId));
    setStaff(null);
    await firebaseSignOut();
  };

  return (
    <RestaurantAuthContext.Provider
      value={{
        staff,
        hotelId,
        restaurantId,
        restaurantName,
        restaurantImage,
        isAuthenticated: !!staff,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </RestaurantAuthContext.Provider>
  );
}

export function useRestaurantAuth() {
  const context = useContext(RestaurantAuthContext);
  if (context === undefined) {
    throw new Error('useRestaurantAuth must be used within a RestaurantAuthProvider');
  }
  return context;
}
